import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowRight, ArrowUpRight, Check, Lock, Search, ShieldCheck, UserRound } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { fcfa, fdate } from "@/lib/format";
import { mediaUrl, menuPages, pathForSlug } from "@/lib/site";
import { useConfig } from "@/context/ConfigContext";
import { Eyebrow, EmptyState, PlusMarks } from "@/components/common";
import { DeviceMock, Phone } from "@/components/public/Mockups";

const Wrap = ({ children, className = "", i = 0 }) => (
  <section className={`max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 mr-fade-up ${className}`} style={{ animationDelay: `${Math.min(i, 4) * 70}ms` }}>{children}</section>
);
const H = ({ b, as: Tag = "h2", className = "mr-h2" }) => (b.title || b.accent ? <Tag className={className}>{b.title} {b.accent && <span className="mr-accent-word">{b.accent}</span>}</Tag> : null);
const Head = ({ b, className = "mb-10 max-w-3xl", as }) => (
  <div className={className}>{b.eyebrow && <Eyebrow className="mb-4">{b.eyebrow}</Eyebrow>}<H b={b} as={as} className={as === "h1" ? "mr-h1" : "mr-h2"} />{b.body && <p className="text-mr-text2 mt-4 text-[17px]">{b.body}</p>}</div>
);
const Img = ({ id, className = "", alt = "" }) => (id ? <img src={mediaUrl(id)} alt={alt} loading="lazy" className={`mr-img object-cover ${className}`} /> : null);
const Cta = ({ label, to = "/demande-invitation", id = "cta" }) => <Link to={to} data-testid={`${id}-button`} className="mr-btn-primary">{label || "Demander une invitation"} <ArrowRight size={16} /></Link>;
const Visual = ({ b, both }) => (b.image ? <div className="relative"><PlusMarks /><div className="border border-mr-border p-2 bg-mr-surface"><Img id={b.image} className="w-full aspect-[4/3]" /></div></div> : b.mockup ? <DeviceMock name={b.mockup} both={both} /> : null);

function Column({ ids, dur, down }) {
  const list = [...ids, ...ids];
  return (
    <div className="flex-1 overflow-hidden">
      <div className={`mr-col flex flex-col gap-3 ${down ? "down" : ""}`} style={{ "--dur": `${dur}s` }}>
        {list.map((id, i) => <Img key={i} id={id} className={`w-full rounded-[3px] ${i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-[4/5]" : "aspect-square"}`} />)}
      </div>
    </div>
  );
}

function HeroMosaic({ b }) {
  const imgs = b.images || [];
  const cols = [0, 1, 2].map((c) => imgs.filter((_, i) => i % 3 === c));
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-16 pb-12 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center" data-testid="hero-mosaic-section">
      <div className="mr-fade-up">
        <Eyebrow className="mb-6">{b.eyebrow}</Eyebrow>
        <H b={b} as="h1" className="mr-h1" />
        <p className="mt-6 text-base lg:text-[17px] text-mr-text2 max-w-xl">{b.body}</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Cta label={b.cta} id="hero-cta" />
          {b.cta2_label && <Link to={b.cta2_link || "/plateforme"} data-testid="hero-secondary-button" className="mr-btn-secondary">{b.cta2_label}</Link>}
        </div>
      </div>
      {imgs.length > 0 && (
        <div className="mr-mosaic flex gap-3 h-[420px] sm:h-[560px] lg:h-[640px]" data-testid="hero-mosaic">
          {cols.filter((c) => c.length).map((c, i) => <Column key={i} ids={c} dur={[70, 58, 82][i]} down={i === 1} />)}
        </div>
      )}
    </section>
  );
}

const PageHeader = ({ b }) => (
  <Wrap className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center pt-12 lg:pt-20">
    <div><Head b={b} as="h1" className="max-w-xl mb-8" />{b.cta && <Cta label={b.cta} id="page-header-cta" />}</div>
    <Visual b={b} both />
  </Wrap>
);

const Partners = ({ b }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/partners").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  if (!items.length) return null;
  const loop = items.length > 6;
  const Logo = ({ p }) => {
    const img = <img src={p.logo_url} alt={p.name} title={p.name} className="h-10 w-auto max-w-[150px] object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity] duration-300" />;
    return p.link ? <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid="partner-logo" className="shrink-0">{img}</a> : <span data-testid="partner-logo" className="shrink-0">{img}</span>;
  };
  return (
    <section className="border-y border-mr-border bg-mr-surface overflow-hidden" data-testid="partners-band">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {b.title && <p className="text-xs uppercase tracking-wider text-mr-muted mb-5 text-center">{b.title}</p>}
        <div className={loop ? "mr-marquee-mask" : ""}>
          <div className={`flex items-center gap-14 ${loop ? "w-max mr-marquee" : "justify-center flex-wrap"}`}>
            {(loop ? [...items, ...items] : items).map((p, k) => <Logo key={`${p.id}-${k}`} p={p} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

const Pillars = ({ b, i }) => (
  <Wrap i={i}><Head b={b} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-mr-border border border-mr-border">
      {b.items.map((it, k) => {
        const Inner = (
          <><span className="font-display font-bold text-4xl text-mr-accent tabular">{String(k + 1).padStart(2, "0")}</span>
            <h3 className="font-display font-semibold text-xl mt-4">{it.title}</h3><p className="text-mr-text2 mt-2 text-[15px]">{it.body}</p>
            {it.link && <span className="inline-flex items-center gap-1 text-sm text-mr-accent mt-4">Découvrir <ArrowUpRight size={14} /></span>}</>
        );
        return it.link ? <Link key={k} to={it.link} data-testid={`pillar-${k + 1}`} className="bg-mr-surface p-7 lg:p-9 hover:bg-mr-raised transition-colors">{Inner}</Link>
          : <div key={k} className="bg-mr-surface p-7 lg:p-9">{Inner}</div>;
      })}
    </div>
  </Wrap>
);

function Feature({ b, i }) {
  const [tab, setTab] = useState(0);
  const tabMocks = ["messages", "voice", "group"];
  const mock = b.tabs ? tabMocks[tab] : b.mockup;
  return (
    <Wrap i={i} className="grid lg:grid-cols-2 gap-12 items-center">
      <div className={b.reverse ? "lg:order-2" : ""}>
        <Head b={b} className="mb-8" />
        {b.tabs ? (
          <div>
            <div className="flex gap-1 border-b border-mr-border mb-5">{b.items.map((it, k) => (
              <button key={k} data-testid={`feature-tab-${k}`} onClick={() => setTab(k)} className={`px-4 min-h-[44px] text-sm border-b-2 -mb-px ${tab === k ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted"}`}>{it.title}</button>))}</div>
            <p className="text-mr-text2">{b.items[tab]?.body}</p>
          </div>
        ) : (
          <ul className="space-y-4">{b.items.map((it, k) => (
            <li key={k} className="flex gap-3"><Check size={18} className="text-mr-accent shrink-0 mt-0.5" /><div><p className="font-medium">{it.title}</p><p className="text-sm text-mr-text2">{it.body}</p></div></li>))}</ul>
        )}
        {b.link && <Link to={b.link} data-testid={`feature-link-${b.link.split("/").pop()}`} className="inline-flex items-center gap-1 mt-7 text-mr-accent font-medium">En savoir plus <ArrowRight size={15} /></Link>}
      </div>
      <div className={b.reverse ? "lg:order-1" : ""}>{b.image ? <Visual b={b} /> : <Phone name={mock} group={mock === "group"} className="mx-auto" />}</div>
    </Wrap>
  );
}

const Proof = ({ b }) => (
  <section className="relative overflow-hidden border-y border-mr-border" data-testid="proof-line">
    {b.image && <Img id={b.image} className="absolute inset-0 w-full h-full" />}
    <div className="absolute inset-0 bg-black/70" />
    <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-28"><p className="font-display font-bold text-2xl sm:text-4xl text-white leading-tight tracking-tight">« {b.body} »</p></div>
  </section>
);

const Transparency = ({ b, i }) => (
  <Wrap i={i}><Head b={b} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{b.items.map((it, k) => (
      <div key={k} className="mr-card"><ShieldCheck className="text-mr-accent mb-4" size={22} /><p className="font-display font-semibold text-lg">{it.title}</p><p className="text-sm text-mr-text2 mt-2">{it.body}</p></div>))}</div>
  </Wrap>
);

const AudienceCard = ({ it }) => (
  <div className="group border border-mr-border bg-mr-surface overflow-hidden" data-testid="audience-card">
    <div className="overflow-hidden"><Img id={it.image} className="w-full aspect-[4/3] group-hover:scale-105 transition-transform duration-700" /></div>
    <div className="p-5"><p className="font-display font-semibold text-lg">{it.title}</p><p className="text-sm text-mr-text2 mt-2">{it.body}</p></div>
  </div>
);

const Audiences = ({ b, i }) => {
  const [open, setOpen] = useState(false);
  const first = b.items.slice(0, 4);
  const more = b.items.slice(4);
  return (
    <Wrap i={i}><Head b={b} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{first.map((it, k) => <AudienceCard key={k} it={it} />)}</div>
      {more.length > 0 && (
        <>
          <div className="grid transition-[grid-template-rows,opacity] duration-500 ease-out" style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }} data-testid="audiences-more" aria-hidden={!open}>
            <div className="overflow-hidden"><div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">{more.map((it, k) => <AudienceCard key={k} it={it} />)}</div></div>
          </div>
          <div className="mt-8 flex justify-center">
            <button type="button" data-testid="audiences-toggle-button" className="mr-btn-secondary" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? "Voir moins" : "Voir plus"}</button>
          </div>
        </>
      )}
    </Wrap>
  );
};

const Stories = ({ b, i }) => (
  <Wrap i={i}><Head b={b} />
    <div className="grid md:grid-cols-3 gap-4">{b.items.map((it, k) => (
      <Link key={k} to={it.link || "#"} data-testid="story-card" className="mr-card p-0 overflow-hidden hover:border-mr-text2 transition-colors">
        <Img id={it.image} className="w-full aspect-[4/3] object-top" />
        <div className="p-5"><p className="text-xs uppercase tracking-wider text-mr-accent">{it.sector}</p><p className="font-display font-semibold text-lg mt-2">{it.name}</p><p className="text-sm text-mr-muted">{it.company}</p><p className="text-sm text-mr-text2 mt-3">{it.result}</p></div>
      </Link>))}</div>
  </Wrap>
);

const Steps = ({ b, i }) => (
  <Wrap i={i}><Head b={b} />
    <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{b.items.map((it, k) => (
      <li key={k} className="border-t-2 border-mr-border pt-5 hover:border-mr-accent transition-colors"><span className="font-display font-bold text-3xl text-mr-muted tabular">{String(k + 1).padStart(2, "0")}</span>
        <h3 className="font-display font-semibold text-lg mt-2">{it.title}</h3>{it.body && <p className="text-sm text-mr-text2 mt-1">{it.body}</p>}</li>))}</ol>
    {b.criteria?.length > 0 && (
      <div className="mr-card mt-12"><p className="mr-eyebrow mb-4">Critères d'admission</p>
        <ul className="grid md:grid-cols-2 gap-3">{b.criteria.map((c) => <li key={c} className="flex gap-2 text-[15px]"><Check size={18} className="text-mr-accent shrink-0" />{c}</li>)}</ul></div>
    )}
  </Wrap>
);

const AppBlock = ({ b, i }) => (
  <Wrap i={i} className="grid lg:grid-cols-2 gap-12 items-center">
    <div><Head b={b} className="mb-6" />
      <div className="grid sm:grid-cols-2 gap-4">{b.items.map((it) => <div key={it.title} className="mr-card"><p className="font-semibold">{it.title}</p><p className="text-sm text-mr-text2 mt-1">{it.body}</p></div>)}</div></div>
    <div className="flex justify-center gap-4"><Phone name="feed" /><Phone name="payments" className="hidden sm:block mt-12" /></div>
  </Wrap>
);

const Faq = ({ b, i }) => (
  <Wrap i={i} className="max-w-4xl"><Head b={b} />
    <Accordion type="single" collapsible className="border-t border-mr-border">{b.items.map((it, k) => (
      <AccordionItem key={k} value={`q${k}`} className="border-b border-mr-border"><AccordionTrigger data-testid={`faq-q-${k}`} className="text-left text-[16px] font-medium py-5 hover:no-underline">{it.title}</AccordionTrigger>
        <AccordionContent className="text-mr-text2 text-[15px] pb-5">{it.body}</AccordionContent></AccordionItem>))}</Accordion>
  </Wrap>
);

const CtaBlock = ({ b }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
    <div className="relative border border-mr-border bg-mr-surface p-10 lg:p-16"><PlusMarks />
      <H b={b} className="mr-h2 sm:text-4xl max-w-2xl" /><p className="text-mr-text2 mt-4 max-w-xl">{b.body}</p><div className="mt-8"><Cta label={b.cta} id="final-cta" /></div></div>
  </section>
);

const Text = ({ b, i }) => (
  <Wrap i={i} className="max-w-4xl">{b.eyebrow && <Eyebrow className="mb-4">{b.eyebrow}</Eyebrow>}<H b={b} />
    <div className="mr-prose text-mr-text2 mt-5 text-[16px]"><ReactMarkdown components={{ a: ({ href, children }) => (href?.startsWith("/") ? <Link to={href}>{children}</Link> : <a href={href}>{children}</a>) }}>{b.body || ""}</ReactMarkdown></div>
  </Wrap>
);

const SubFeatures = ({ b }) => b.items.map((it, k) => (
  <Wrap key={k} i={k} className="grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-16">
    <div className={k % 2 ? "lg:order-2" : ""}><span className="font-mono text-sm text-mr-accent">{String(k + 1).padStart(2, "0")}</span><h3 className="mr-h2 mt-2">{it.title}</h3><p className="text-mr-text2 mt-4 text-[17px] max-w-lg">{it.body}</p></div>
    <div className={k % 2 ? "lg:order-1" : ""}><Visual b={it} /></div>
  </Wrap>
));

function Related({ b, slug }) {
  const { pages } = useConfig();
  const list = menuPages(pages, "plateforme").filter((p) => p.slug !== slug);
  return (
    <Wrap><Head b={b} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{list.map((p) => (
        <Link key={p.slug} to={pathForSlug(p.slug)} data-testid={`related-${p.slug.split("/").pop()}`} className="mr-card p-4 hover:border-mr-text2 transition-colors"><p className="font-medium">{p.nav_label}</p><p className="text-xs text-mr-muted mt-1">{p.tagline}</p></Link>))}</div>
    </Wrap>
  );
}

function Listing({ b }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/listing", { params: { type: b.kind } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [b.kind]);
  return (
    <Wrap><Head b={b} />
      {items && items.length === 0 ? <EmptyState title="Aucune date publique pour le moment" text={b.empty} testId="listing-empty" /> : (
        <ul className="divide-y divide-mr-border border-y border-mr-border">{(items || []).map((e) => (
          <li key={e.id} className="py-5 flex justify-between gap-4" data-testid="listing-item"><div><p className="font-display font-semibold text-lg">{e.title}</p><p className="text-sm text-mr-muted">{e.summary || e.location}</p></div>{e.date && <span className="tabular text-sm text-mr-text2">{fdate(e.date)}</span>}</li>))}</ul>
      )}
    </Wrap>
  );
}

export function BlogList({ b }) {
  const { settings } = useConfig();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(b.category || "");
  const [gt, setGt] = useState("");
  const guides = b.category === "Guides";
  const [data, setData] = useState(null);
  const more = () => api.get("/public/articles", { params: { q, category: cat, guide_type: gt, skip: data.items.length } }).then(({ data: d }) => setData({ ...d, items: [...data.items, ...d.items] })).catch(() => {});
  useEffect(() => {
    const t = setTimeout(() => api.get("/public/articles", { params: { q, category: cat, guide_type: gt } }).then(({ data: d }) => setData(d)).catch(() => setData({ items: [] })), 250);
    return () => clearTimeout(t);
  }, [q, cat, gt]);
  return (
    <Wrap className="!pt-0">
      {!b.category && (
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative w-full sm:w-72"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-mr-muted" /><input data-testid="blog-search-input" className="mr-input pl-9" placeholder="Rechercher un article" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          {["", ...(settings["site.blog_categories"] || [])].map((c) => <button key={c || "all"} data-testid={`blog-cat-${c || "all"}`} onClick={() => setCat(c)} className={`px-3 h-11 rounded-full border text-sm ${cat === c ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>{c || "Tous"}</button>)}
        </div>
      )}
      {guides && (
        <div className="flex flex-wrap gap-3 mb-8" data-testid="guide-type-filter">
          {["", ...(settings["site.guide_types"] || [])].map((t) => <button key={t || "all"} data-testid={`guide-type-${t || "all"}`} onClick={() => setGt(t)} className={`px-3 h-11 rounded-full border text-sm ${gt === t ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>{t || "Tous les guides"}</button>)}
        </div>
      )}
      {data && data.items.length === 0 ? <EmptyState title="Aucun article" text="Les articles publiés apparaîtront ici." testId="blog-empty" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{(data?.items || []).map((a) => (
          <Link key={a.id} to={`/ressources/blog/${a.slug}`} data-testid="blog-card" className="group border border-mr-border bg-mr-surface hover:border-mr-text2 transition-colors">
            <div className="overflow-hidden"><Img id={a.cover_media_id} className="w-full aspect-[16/10] group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="p-5"><div className="flex items-center gap-2 text-xs text-mr-muted"><span className="text-mr-accent">{a.guide_type ? `${a.category} · ${a.guide_type}` : a.category}</span><span>·</span><span className="tabular">{fdate(a.published_at)}</span>{a.visibility === "membres" && <span className="ml-auto inline-flex items-center gap-1"><Lock size={11} /> Membres</span>}</div>
              <p className="font-display font-semibold text-lg mt-2">{a.title}</p><p className="text-sm text-mr-text2 mt-2">{a.excerpt}</p></div>
          </Link>))}</div>
      )}
      {data && data.total > data.items.length && (
        <div className="text-center mt-8"><button data-testid="blog-load-more" className="mr-btn-secondary px-6" onClick={more}>Voir plus d'articles</button></div>
      )}
    </Wrap>
  );
}

const Founder = ({ b }) => (
  <Wrap className="grid md:grid-cols-[320px_1fr] gap-10 items-start pt-12 lg:pt-20">
    <div className="relative"><PlusMarks />{b.image ? <Img id={b.image} className="w-full aspect-[3/4] border border-mr-border" /> : <div className="aspect-[3/4] border border-dashed border-mr-border grid place-items-center text-mr-muted"><div className="text-center"><UserRound size={40} className="mx-auto" /><p className="text-xs mt-2">[Photo du fondateur]</p></div></div>}</div>
    <div><Eyebrow className="mb-4">{b.eyebrow}</Eyebrow><h1 className="mr-h1">{b.name}</h1><p className="text-mr-accent mt-3">{b.role}</p><p className="text-mr-text2 mt-6 text-[17px] max-w-2xl whitespace-pre-line">{b.body}</p></div>
  </Wrap>
);

function FeeNotice({ b }) {
  const { settings } = useConfig();
  return (
    <Wrap className="max-w-4xl"><div className="mr-card p-8" data-testid="fee-notice"><Eyebrow className="mb-3">{b.eyebrow}</Eyebrow><h2 className="mr-h2">{b.title}</h2><p className="text-mr-text2 mt-4">{b.body}</p>
      {settings["membership.annual_fee"] != null && <p className="font-display font-bold text-3xl tabular mt-6" data-testid="fee-public-amount">{fcfa(settings["membership.annual_fee"])} / an</p>}</div></Wrap>
  );
}

const MAP = { hero_mosaic: HeroMosaic, page_header: PageHeader, trust: () => null, partners: Partners, pillars: Pillars, feature: Feature, proof: Proof, transparency: Transparency,
  audiences: Audiences, stories: Stories, steps: Steps, app: AppBlock, faq: Faq, cta: CtaBlock, text: Text, subfeatures: SubFeatures, related: Related,
  listing: Listing, blog_list: BlogList, founder: Founder, fee_notice: FeeNotice };

export const BLOCK_TYPES = Object.keys(MAP);

export const Sections = ({ blocks, slug }) => blocks.map((b, i) => {
  const C = MAP[b.type] || Text;
  return <C key={i} b={{ items: [], ...b }} i={i} slug={slug} />;
});
